/**
 * BACK ON TRACK — серверное ядро партии.
 *
 * Канон механики — раздел Game Rules в README.md. Ссылки вида R42 указывают
 * на его правила.
 * Модуль не знает про Socket.IO и про рендер: он складывает исходящие события
 * в outbox, а транспорт их разбирает.
 *
 * Четыре принципа, на которых держится файл:
 *  - Журнал ходов — единственный источник истины об истории партии (R88).
 *    Статусы ловушек, показанные вопросы и статистика не хранятся, а выводятся
 *    из журнала: расходиться с историей им негде.
 *  - Позиция игрока — одно поле node (R92). Клетка, глубина и болото
 *    выводятся из идентификатора узла, дублирующих полей нет.
 *  - Всё состояние партии сериализуемо целиком (R94): ни Map, ни Set.
 *  - Правильные ответы и объяснения не покидают сервер до раскрытия хода (R85).
 */

// ============================================================================
// 1. БАЗОВЫЕ ТИПЫ
// ============================================================================

export type PlayerId = string
export type GameId = string
export type NodeId = string

/** Типы клеток. Закрытый перечень из шести, R23-R28. */
export const CellType = {
  START: 'START',
  NORMAL: 'NORMAL',
  TRAP: 'TRAP',
  LUCK: 'LUCK',
  SETBACK: 'SETBACK',
  SUCCESS: 'SUCCESS',
} as const
export type CellType = (typeof CellType)[keyof typeof CellType]

/** Статус ловушки у одного игрока. Выводится из журнала, R60, R89. */
export const TrapStatus = {
  UNSEEN: 'UNSEEN',
  SEEN: 'SEEN',
  MASTERED: 'MASTERED',
} as const
export type TrapStatus = (typeof TrapStatus)[keyof typeof TrapStatus]

/** Фазы движка. Анимационные состояния клиента сюда не входят, R102. */
export const Phase = {
  LOBBY: 'LOBBY',
  AWAIT_ROLL: 'AWAIT_ROLL',
  AWAIT_ANSWER: 'AWAIT_ANSWER',
  TURN_RESULT: 'TURN_RESULT',
  FINISHED: 'FINISHED',
} as const
export type Phase = (typeof Phase)[keyof typeof Phase]

/** Что произошло за ход. Клиент по этому значению выбирает анимацию и звук. */
export const TurnEvent = {
  NONE: 'none',
  LUCK: 'luck',
  SETBACK: 'setback',
  TRAP_FIRST: 'trap_first',
  TRAP_MASTERED: 'trap_mastered',
  ANSWER_WIN: 'answer_win',
  ANSWER_FAIL: 'answer_fail',
  SWAMP_CLIMB: 'swamp_climb',
  SWAMP_EXIT: 'swamp_exit',
  FINISH: 'finish',
  /** Игрок не бросил кубик: остаётся на месте, очередь идёт дальше, R41. */
  ROLL_TIMEOUT: 'roll_timeout',
  /** Игрок не ответил на вопрос: падает на дно, R42, R78. */
  ANSWER_TIMEOUT: 'answer_timeout',
} as const
export type TurnEvent = (typeof TurnEvent)[keyof typeof TurnEvent]

/** Предел длины имени игрока. Клиент режет ввод по нему же. */
export const NAME_MAX = 12
/** Предел длины названия комнаты. */
export const ROOM_NAME_MAX = 24

/**
 * Текст игрока для показа другим: нормализованный, без управляющих и невидимых
 * знаков (в том числе разворота письма), с одиночными пробелами, R122. Имя из
 * одних невидимых знаков иначе выглядело бы пустым, а «\u202e» разворачивало бы
 * соседний текст. NFKC сводит «Ｃｌａｕｄｅ» к «Claude»: двойник по виду не пройдёт
 * проверку уникальности.
 */
export function cleanText(value: unknown): string {
  if (typeof value !== 'string') return ''
  return value
    .normalize('NFKC')
    .replace(/[\p{Cc}\p{Cf}\p{Zl}\p{Zp}]/gu, '')
    .replace(/\s+/g, ' ')
    .trim()
}

/** Идентификатор рисунка аватарки: латиница, цифры, дефис и подчёркивание, R122. */
export const AVATAR_PATTERN = /^[A-Za-z0-9_-]{1,40}$/

/**
 * Сколько комнат живёт на сервере одновременно, R118. Список лобби — это экран,
 * а не каталог: больше пяти строк он не показывает, и новую комнату при полном
 * списке не создают. Клиент прячет поле создания по тому же числу.
 */
export const MAX_ROOMS = 5

/*
 * Фишки, шесть: они различаются и цветом, и формой, R31, R32. Сервер знает только имя фишки и её
 * форму; сам цвет — из палитры макета (кадр `palette`) и лежит один раз, в `--color-marker-<имя>` в
 * `index.main.css`. Имя фишки и имя цвета в палитре совпадают, поэтому поле, лобби и диалоги
 * показывают один и тот же цвет без перевода.
 *
 * Шесть, а не восемь. Красный ушёл в акцент: он означает действие и чей ход, и фишка того же цвета
 * спорила бы с этим. Лаймовый убран следом — он сидел между охрой и зелёным и от обоих отличался
 * хуже прочих. Шесть — ещё и ровно столько, сколько аватарок в строке, так что оба ряда в лобби
 * становятся одной сеткой.
 */
export const PAWNS = [
  { id: 'amber', shape: 'square' },
  { id: 'green', shape: 'hexagon' },
  { id: 'teal', shape: 'circle' },
  { id: 'blue', shape: 'cross' },
  { id: 'violet', shape: 'pentagon' },
  { id: 'pink', shape: 'star' },
] as const
export type PawnId = (typeof PAWNS)[number]['id']

// ============================================================================
// 2. ДЕТЕРМИНИРОВАННЫЙ ГСЧ
// ============================================================================

/** mulberry32 без внутреннего состояния: всё, что нужно, приходит аргументом. */
function mulberry32(seed: number): number {
  let a = (seed >>> 0) | 0
  a = (a + 0x6d2b79f5) | 0
  let t = Math.imul(a ^ (a >>> 15), 1 | a)
  t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t
  return ((t ^ (t >>> 14)) >>> 0) / 4294967296
}

const GOLDEN = 0x9e3779b9

/**
 * Курсорный генератор для одноразовых задач вроде сборки поля. Открыт
 * наружу: клиент сеет им свои декорации, и второй копии этой арифметики в
 * проекте быть не должно — перенабранное по памяти битовое месиво однажды
 * разойдётся, и разойдётся молча.
 */
export function rngFrom(seed: number): () => number {
  let cursor = 0
  return () => mulberry32(seed + cursor++ * GOLDEN)
}

// ============================================================================
// 3. ПОЛЕ: ПЛАН, ГЕНЕРАТОР, ГРАФ
// ============================================================================

export interface SwampPlan {
  id: string
  /** Категория вопросов этого болота. Одна на болото и не меняется, R80. */
  trapId: string
  /** Индекс клетки основной линии, под которой висит болото. */
  entry: number
  depth: number
}

/**
 * Компактный план поля. Из него детерминированно строится граф,
 * поэтому в состоянии партии лежит план, а не развёрнутые узлы.
 */
export interface BoardPlan {
  id: string
  title: string
  seed: number
  /** Узлов на основной линии вместе со START и SUCCESS, R12. */
  mainLength: number
  swamps: SwampPlan[]
  /** Индексы клеток LUCK, по одной на болото, R17. */
  luck: number[]
  /** Индексы клеток SETBACK, по одной на болото, R17. */
  setback: number[]
  /** На сколько клеток клевер рвёт вперёд. Часть плана: граф строится по нему, а план хранится с партией. */
  luckSteps: number
  /** На сколько клеток мечи откатывают назад. */
  setbackSteps: number
}

export interface BoardNode {
  id: NodeId
  type: CellType
  /** Колонка: индекс клетки основной линии. */
  x: number
  /** Ряд: 0 — поверхность, 1..depth — глубина в болоте. */
  y: number
  /** У TRAP — её болото. У LUCK и SETBACK — болото, которому клетка придана. */
  swampId?: string
}

export interface BoardEdge {
  from: NodeId
  to: NodeId
  /*
   * next — вперёд по линии, up — вверх по колодцу, fall — падение на дно.
   * Два переноса внутри колодца: throw — откат назад, в предыдущий колодец
   * (а из первого — на старт), boost — выброс вверх и вперёд, на поверхность.
   */
  kind: 'next' | 'up' | 'fall' | 'throw' | 'boost'
}

export interface Board {
  id: string
  title: string
  seed: number
  nodes: BoardNode[]
  edges: BoardEdge[]
  swamps: SwampPlan[]
  /** Узлы основной линии по порядку. Индекс массива равен x. */
  mainPath: NodeId[]
  startId: NodeId
  finishId: NodeId
}

/** Правила генерации поля, R12-R19. Значения по умолчанию — канон MVP. */
export interface BoardRules {
  mainLength: number
  /** Глубины колодцев слева направо, длина массива задаёт их число. */
  swampDepths: number[]
  /*
   * Просветы в клетках. Ноль всюду означает «правила нет»: отдельных
   * выключателей у них не заводим, иначе одно и то же правило живёт в двух
   * местах и они расходятся.
   */
  minSwampGap: number
  /** Клеток между стартом и первым колодцем. */
  headRoom: number
  /** Клеток между последним колодцем и финишем: выбросу нужна клетка. */
  tailRoom: number
  /** Просвет между колодцем и клеткой мечей. */
  minSetbackGap: number
  /** Просвет между соседними клетками мечей. */
  minFightGap: number
  /** Просвет между соседними клетками клевера. */
  minLuckGap: number
  /** Просвет между клевером и мечами. */
  minEventGap: number
  /** На сколько клеток клевер рвёт вперёд. */
  luckSteps: number
  /** На сколько клеток мечи откатывают назад. */
  setbackSteps: number
  /** Сколько раз пробовать, прежде чем признать набор правил невыполнимым. */
  maxAttempts: number
}

export const DEFAULT_BOARD_RULES: BoardRules = {
  mainLength: 33,
  swampDepths: [7, 4, 6, 5, 8],
  minSwampGap: 3,
  headRoom: 1,
  tailRoom: 2,
  minSetbackGap: 1,
  minFightGap: 1,
  minLuckGap: 4,
  minEventGap: 1,
  luckSteps: 2,
  setbackSteps: 2,
  /*
   * Запас попыток. Просветы почти насыщают линию: при 500 попытках четыре
   * seed из тысячи не собирались. Сборка одной раскладки — десятки
   * микросекунд, так что запас дешевле разжижения правил.
   */
  maxAttempts: 2000,
}

const pad = (n: number): string => String(n).padStart(2, '0')
export const mainNodeId = (x: number): NodeId => `c${pad(x)}`
export const swampNodeId = (entry: number, level: number): NodeId => `w${pad(entry)}-${pad(level)}`

/*
 * Куда на самом деле приводит перенос. Правила два: встать можно только на
 * нейтральную клетку, и одна клетка принимает не больше одного переноса —
 * иначе события выстраиваются в цепочку, а на поле в одну клетку целятся
 * несколько стрелок. От расчётной цели идём дальше в ту же сторону, пока не
 * найдём свободную проходную клетку.
 *
 * Если в эту сторону нейтральных не осталось, остаёмся на краю: так выходит у
 * самой первой клетки, за которой только старт, и у последней, за которой
 * только финиш. Раскладка эти два случая не закрывает.
 */
function neutralLanding(
  surface: CellType[],
  from: number,
  target: number,
  dir: 1 | -1,
  taken: ReadonlySet<number> = new Set(),
): number {
  const last = surface.length - 1
  // Перенос не меняет направления: рывок всегда вперёд от своей клетки,
  // откат всегда назад. Окно поиска этим и задано.
  const lo = dir > 0 ? Math.min(from + 1, last) : 0
  const hi = dir > 0 ? last : Math.max(from - 1, 0)
  const start = Math.min(Math.max(target, lo), hi)

  // Сначала дальше от клетки, потом обратно к ней: ближе расчётной цели не
  // встаём без нужды, но и за край окна не уходим.
  const order: number[] = []
  for (let x = start; x >= lo && x <= hi; x += dir) order.push(x)
  for (let x = start - dir; x >= lo && x <= hi; x -= dir) order.push(x)

  const free = order.find((x) => surface[x] === CellType.NORMAL && !taken.has(x))
  if (free !== undefined) return free
  const neutral = order.find((x) => surface[x] === CellType.NORMAL)
  return neutral ?? start
}

/** Уровни двух особых клеток одного колодца: откат и выброс. */
interface WellLevels {
  back: number
  boost: number
}

/*
 * Раскладка особых клеток по колодцам. Три требования сходятся в одном месте:
 *
 *  - откат рисуется строго горизонтальной стрелкой, значит приземление лежит
 *    на том же уровне в предыдущем колодце — и этот уровень обязан быть там
 *    обычной клеткой, иначе откаты сцепляются в цепочку;
 *  - у каждого колодца свой уровень отката, иначе весь ряд стрелок
 *    вырождается в одну прямую через всё поле;
 *  - ни откат, ни выброс не стоят на дне: туда приводит падение, и клетка
 *    срабатывала бы при каждом попадании в колодец.
 *
 * Перебор жадный, сверху вниз: уровень отката — самый глубокий из свободных,
 * какой ещё дотягивается до предыдущего колодца. Требования ослабляются по
 * очереди, если выбора не осталось, — генератор не должен падать на чужих
 * глубинах.
 */
function wellLevels(swamps: SwampPlan[]): WellLevels[] {
  const out: WellLevels[] = []
  const usedBack = new Set<number>()

  swamps.forEach((swamp, i) => {
    const prev = swamps[i - 1]
    const taken = i > 0 ? [out[i - 1].back, out[i - 1].boost] : []
    // Дальше дна предыдущего колодца стрелка не дотянется, там нет клетки.
    const reach = Math.min(swamp.depth - 1, prev ? prev.depth : swamp.depth)

    const pick = (unique: boolean): number | undefined => {
      for (let level = reach; level >= 1; level--) {
        if (taken.includes(level)) continue
        if (unique && usedBack.has(level)) continue
        return level
      }
      return undefined
    }
    const back = pick(true) ?? pick(false) ?? 1
    usedBack.add(back)

    let boost = Math.max(1, swamp.depth - 1)
    if (boost === back) boost = back > 1 ? back - 1 : Math.min(back + 1, swamp.depth - 1)
    out.push({ back, boost })
  })

  return out
}

/** Тип клетки колодца: откат — мечи, выброс — клевер, остальное — проход. */
const wellCellType = (level: number, levels: WellLevels): CellType => {
  if (level === levels.back) return CellType.SETBACK
  if (level === levels.boost) return CellType.LUCK
  return CellType.NORMAL
}

/**
 * Позиция игрока выводится из идентификатора узла, R93.
 * Отдельных полей cellIndex, depth и swampId у игрока нет.
 */
export function positionOf(node: NodeId): { cellIndex: number; depth: number } {
  if (node[0] === 'c') return { cellIndex: Number(node.slice(1)), depth: 0 }
  return { cellIndex: Number(node.slice(1, 3)), depth: Number(node.slice(4)) }
}

/** Выбирает k позиций в [lo..hi] так, что соседние отличаются не меньше чем на gap+1. */
function pickSpaced(rand: () => number, lo: number, hi: number, k: number, gap: number): number[] | null {
  const step = gap + 1
  const slack = hi - lo + 1 - (k - 1) * step
  if (slack < 1) return null
  const offsets = Array.from({ length: k }, () => Math.floor(rand() * slack)).sort((a, b) => a - b)
  return offsets.map((v, i) => lo + v + i * step)
}

/*
 * Выбор k клеток из произвольного набора так, чтобы между соседними было не
 * меньше `gap` клеток. Берём случайную клетку и выбрасываем всё, что к ней
 * прижалось; жадность иногда заводит в тупик, поэтому даём несколько заходов.
 *
 * Это замена прежнему приёму «выбрать как попало, потом растащить»: доводка
 * после выбора съедала случайность — на 2000 seed клевер давал всего 131
 * разный набор и липнул к правому краю.
 */
function pickApart(rand: () => number, pool: number[], k: number, gap: number, tries = 24): number[] | null {
  if (k === 0) return []
  for (let t = 0; t < tries; t++) {
    const free = [...pool]
    const out: number[] = []
    while (out.length < k && free.length > 0) {
      const x = free.splice(Math.floor(rand() * free.length), 1)[0]
      out.push(x)
      for (let i = free.length - 1; i >= 0; i--) if (Math.abs(free[i] - x) <= gap) free.splice(i, 1)
    }
    if (out.length === k) return out.sort((a, b) => a - b)
  }
  return null
}

function shuffle<T>(rand: () => number, items: T[]): T[] {
  const copy = [...items]
  for (let i = copy.length - 1; i > 0; i--) {
    const j = Math.floor(rand() * (i + 1))
    ;[copy[i], copy[j]] = [copy[j], copy[i]]
  }
  return copy
}

/**
 * Сборка плана поля из seed, R14-R19.
 * Болота расставляются с просветом, затем ищутся клетки SETBACK на удалении
 * от каждого болота, затем в оставшихся клетках размещаются LUCK.
 * Не всякая расстановка болот оставляет достаточно места, поэтому попытки повторяются.
 */
export function generateBoardPlan(
  seed: number,
  trapIds: string[],
  rules: BoardRules = DEFAULT_BOARD_RULES,
  meta: { id?: string; title?: string } = {},
): BoardPlan {
  const count = rules.swampDepths.length
  if (trapIds.length < count)
    throw new Error(`Нужно минимум ${count} категорий ловушек, дано ${trapIds.length}`)

  const rand = rngFrom(seed)
  const last = rules.mainLength - 1
  const chosenTraps = shuffle(rand, trapIds).slice(0, count)

  /*
   * Каждое правило раскладки применяется при выборе, а не доводкой после
   * него. Доводка неизбежно смещает результат к краю диапазона: прежний
   * вариант ставил последний колодец на одну и ту же клетку в 70 % партий.
   */
  const first = 1 + rules.headRoom
  const lastEntry = last - 1 - rules.tailRoom

  for (let attempt = 0; attempt < rules.maxAttempts; attempt++) {
    const entries = pickSpaced(rand, first, lastEntry, count, rules.minSwampGap)
    if (!entries) break

    // Мечи держатся от колодцев и друг от друга; клевер — от клевера.
    const nearSwamp = new Set<number>()
    for (const e of entries)
      for (let d = -rules.minSetbackGap; d <= rules.minSetbackGap; d++) nearSwamp.add(e + d)

    /*
     * Границы событий — не отдельное правило, а следствие переноса: мечам
     * нужно куда откатывать, клеверу — куда рвануть. Отсюда мечи не ближе
     * длины отката к старту, а клевер не ближе длины рывка к финишу.
     */
    const fightFrom = 1 + rules.setbackSteps
    const luckTo = last - 1 - rules.luckSteps

    const forFight: number[] = []
    for (let i = fightFrom; i < last; i++) if (!nearSwamp.has(i)) forFight.push(i)
    const setback = pickApart(rand, forFight, count, rules.minFightGap)
    if (!setback) continue

    /*
     * Клевер держится не только от клевера, но и от мечей: рядом стоящие
     * события читаются как одна опасная зона. Порядок проверен замером —
     * если выбирать клевер первым, мечам не остаётся места вовсе.
     */
    const busy = new Set<number>([...entries, ...setback])
    for (const sb of setback) for (let d = -rules.minEventGap; d <= rules.minEventGap; d++) busy.add(sb + d)
    const forLuck: number[] = []
    for (let i = 1; i <= luckTo; i++) if (!busy.has(i)) forLuck.push(i)
    const luck = pickApart(rand, forLuck, count, rules.minLuckGap)
    if (!luck) continue

    const candidate: BoardPlan = {
      id: meta.id ?? `board_${seed.toString(36)}`,
      title: meta.title ?? 'BACK ON TRACK',
      seed,
      mainLength: rules.mainLength,
      // Глубины идут слева направо в порядке возрастания позиции, R15.
      swamps: entries.map((entry, i) => ({
        id: `sw${pad(entry)}`,
        trapId: chosenTraps[i],
        entry,
        depth: rules.swampDepths[i],
      })),
      luck,
      setback,
      luckSteps: rules.luckSteps,
      setbackSteps: rules.setbackSteps,
    }
    /*
     * Куда ведут стрелки, видно только на графе: собираем и проверяем. Сюда
     * же попадают случаи, где переносу некуда целиться, — например, мечи в
     * самом начале линии, откатывать с которых просто некуда.
     */
    if (teleportProblems(createBoard(candidate)).length > 0) continue
    return candidate
  }

  throw new Error('Не удалось собрать поле по заданным правилам')
}

/*
 * Проверяет правила переносов на собранном графе: цель нейтральна и принимает
 * не больше одной стрелки.
 *
 * Край линии не в счёт, и это не лень, а арифметика. Переносов, целящих на
 * поверхность, ровно `3k + 1` при `k` колодцах, а проходных клеток на линии
 * длины `L` остаётся `L - 2 - 3k`. Уникальность возможна только при
 * `L >= 6k + 3`: для пяти колодцев это 33 клетки, а линия у нас 31. Пока
 * длина или число колодцев не изменятся, паре переносов деться некуда, и
 * упираются они всегда в торец.
 *
 * Считать это можно только по готовому графу: цели раздаются разом на всё
 * поле. Поэтому генератор собирает граф прямо в переборе и отбраковывает
 * раскладку, на которой правила не сходятся, — чаще всего это тесная корма,
 * где за выброс из последнего колодца спорят клевер и сам колодец.
 */
export function teleportProblems(board: Board): string[] {
  const byId = new Map(board.nodes.map((n) => [n.id, n]))
  const aimed = new Set<string>()
  const problems: string[] = []
  const last = board.mainPath.length - 1
  for (const edge of board.edges) {
    if (edge.kind !== 'throw' && edge.kind !== 'boost') continue
    const to = byId.get(edge.to)
    if (!to) {
      problems.push(`${edge.from}: цели ${edge.to} нет на поле`)
      continue
    }
    if (to.y === 0 && (to.x === 0 || to.x === last)) continue
    if (to.type !== CellType.NORMAL) problems.push(`${edge.from} → ${edge.to}: цель ${to.type}`)
    if (aimed.has(edge.to)) problems.push(`в ${edge.to} целятся две стрелки`)
    aimed.add(edge.to)
  }
  return problems
}

/** Разворачивает план в граф. Чистая функция: один план даёт один и тот же граф. */
export function createBoard(plan: BoardPlan): Board {
  const nodes: BoardNode[] = []
  const edges: BoardEdge[] = []
  const mainPath: NodeId[] = []

  const swampByEntry = new Map(plan.swamps.map((s) => [s.entry, s]))
  const luckOwner = new Map(plan.luck.map((x, i) => [x, plan.swamps[i]?.id]))
  const setbackOwner = new Map(plan.setback.map((x, i) => [x, plan.swamps[i]?.id]))
  const last = plan.mainLength - 1

  for (let x = 0; x < plan.mainLength; x++) {
    let type: CellType = CellType.NORMAL
    if (x === 0) type = CellType.START
    else if (x === last) type = CellType.SUCCESS
    else if (swampByEntry.has(x)) type = CellType.TRAP
    else if (luckOwner.has(x)) type = CellType.LUCK
    else if (setbackOwner.has(x)) type = CellType.SETBACK

    const node: BoardNode = { id: mainNodeId(x), type, x, y: 0 }
    const owner = swampByEntry.get(x)?.id ?? luckOwner.get(x) ?? setbackOwner.get(x)
    if (owner) node.swampId = owner
    nodes.push(node)
    mainPath.push(node.id)
    if (x > 0) edges.push({ from: mainNodeId(x - 1), to: node.id, kind: 'next' })
  }

  const levels = wellLevels(plan.swamps)

  plan.swamps.forEach((swamp, i) => {
    for (let level = 1; level <= swamp.depth; level++) {
      nodes.push({
        id: swampNodeId(swamp.entry, level),
        type: wellCellType(level, levels[i]),
        x: swamp.entry,
        y: level,
        swampId: swamp.id,
      })
      // Подъём: со дна к поверхности по одной клетке за шаг кубика, R67.
      const above = level === 1 ? mainNodeId(swamp.entry) : swampNodeId(swamp.entry, level - 1)
      edges.push({ from: swampNodeId(swamp.entry, level), to: above, kind: 'up' })
    }
    // Падение идёт сразу на дно, промежуточные клетки не проходятся, R66.
    edges.push({ from: mainNodeId(swamp.entry), to: swampNodeId(swamp.entry, swamp.depth), kind: 'fall' })
  })

  /** Типы клеток поверхности: по ним и ищется нейтральная цель переноса. */
  const surface = nodes.slice(0, plan.mainLength).map((n) => n.type)
  /** Клетки поверхности, куда перенос уже целится: второй туда нельзя. */
  const taken = new Set<number>()
  const claim = (x: number): number => (taken.add(x), x)
  /** Куда ведёт перенос: только проходная клетка и только своя. */
  const landingOf = (from: number, target: number, dir: 1 | -1): number =>
    neutralLanding(surface, from, target, dir, taken)

  /*
   * Переносы внутри колодца. Отдельным проходом, после всех дорожек: откат
   * смотрит на предыдущий колодец, а тот должен быть уже собран.
   */
  plan.swamps.forEach((swamp, i) => {
    const { back, boost } = levels[i]
    if (back === boost) return

    const prev = plan.swamps[i - 1]
    /*
     * Приземление ровно на том же уровне: стрелка отката горизонтальна.
     *
     * У первого колодца предыдущего нет, и отбрасывает он на старт. Это
     * единственное место, где цель не проходная: у первой клетки линии просто
     * нет ничего позади.
     */
    const landing = prev ? swampNodeId(prev.entry, back) : mainNodeId(claim(0))
    edges.push({ from: swampNodeId(swamp.entry, back), to: landing, kind: 'throw' })
    // Выброс — на поверхность, на ближайшую нейтральную клетку вперёд.
    edges.push({
      from: swampNodeId(swamp.entry, boost),
      to: mainNodeId(claim(landingOf(swamp.entry, swamp.entry + 1, 1))),
      kind: 'boost',
    })
  })

  /*
   * Переносы на самой поверхности: клевер тянет вперёд, мечи отбрасывают
   * назад. Это те же два вида, только вдоль ряда, поэтому и kind тот же.
   * Длину шага берём из плана: он хранится с партией, и граф после восстановления тот же.
   */
  for (let x = 1; x < last; x++) {
    const node = nodes[x]
    if (node.type === CellType.LUCK) {
      const target = claim(landingOf(x, x + plan.luckSteps, 1))
      if (target !== x) edges.push({ from: node.id, to: mainNodeId(target), kind: 'boost' })
    } else if (node.type === CellType.SETBACK) {
      const target = claim(landingOf(x, x - plan.setbackSteps, -1))
      if (target !== x) edges.push({ from: node.id, to: mainNodeId(target), kind: 'throw' })
    }
  }

  return {
    id: plan.id,
    title: plan.title,
    seed: plan.seed,
    nodes,
    edges,
    swamps: plan.swamps,
    mainPath,
    startId: mainPath[0],
    finishId: mainPath[last],
  }
}

/** Проверка плана перед стартом партии. Невалидный план роняет создание игры. */
export function validateBoardPlan(plan: BoardPlan, rules: BoardRules = DEFAULT_BOARD_RULES): string[] {
  const errors: string[] = []
  const last = plan.mainLength - 1

  if (plan.mainLength !== rules.mainLength)
    errors.push(`Основная линия ${plan.mainLength} узлов вместо ${rules.mainLength}`)
  if (plan.swamps.length !== rules.swampDepths.length)
    errors.push(`Болот ${plan.swamps.length} вместо ${rules.swampDepths.length}`)

  const entries = plan.swamps.map((s) => s.entry)
  const sorted = [...entries].sort((a, b) => a - b)
  if (String(sorted) !== String(entries)) errors.push('Болота идут не по возрастанию позиции')
  for (let i = 1; i < sorted.length; i++) {
    if (sorted[i] - sorted[i - 1] <= rules.minSwampGap)
      errors.push(`Болота ${sorted[i - 1]} и ${sorted[i]} стоят ближе ${rules.minSwampGap} клеток`)
  }
  plan.swamps.forEach((s, i) => {
    if (s.depth !== rules.swampDepths[i])
      errors.push(`Болото ${s.id}: глубина ${s.depth} вместо ${rules.swampDepths[i]}`)
    if (s.entry < 1 || s.entry > last - 1) errors.push(`Болото ${s.id} вне игровых клеток`)
  })

  const trapIds = new Set(plan.swamps.map((s) => s.trapId))
  if (trapIds.size !== plan.swamps.length) errors.push('Два болота делят одну категорию вопросов')

  /*
   * Подбор держит зазор `minSetbackGap`, но это предпочтение, а не предел
   * годности: последний колодец отодвигается от финиша уже после подбора и
   * может подойти к мечам ближе. Недопустимо только вплотную — тогда откат
   * высаживал бы прямо у входа в колодец.
   */
  for (const sb of plan.setback) {
    for (const e of entries) {
      if (Math.abs(sb - e) <= 1) errors.push(`Клетка SETBACK ${sb} вплотную к болоту ${e}`)
    }
  }

  for (const e of entries) {
    if (e < 1 + rules.headRoom || e > last - 1 - rules.tailRoom)
      errors.push(`Болото ${e} ближе положенного к торцу`)
  }

  const fights = [...plan.setback].sort((a, b) => a - b)
  for (let i = 1; i < fights.length; i++) {
    const gap = fights[i] - fights[i - 1] - 1
    if (gap < rules.minFightGap) errors.push(`Между SETBACK ${fights[i - 1]} и ${fights[i]} просвет ${gap}`)
  }

  for (const lu of plan.luck) {
    for (const sb of plan.setback) {
      const gap = Math.abs(lu - sb) - 1
      if (gap < rules.minEventGap) errors.push(`Между LUCK ${lu} и SETBACK ${sb} просвет ${gap}`)
    }
  }

  const clovers = [...plan.luck].sort((a, b) => a - b)
  for (let i = 1; i < clovers.length; i++) {
    const gap = clovers[i] - clovers[i - 1] - 1
    if (gap < rules.minLuckGap)
      errors.push(`Между LUCK ${clovers[i - 1]} и ${clovers[i]} просвет ${gap}, нужен ${rules.minLuckGap}`)
  }

  const occupied = [...entries, ...plan.luck, ...plan.setback]
  if (new Set(occupied).size !== occupied.length) errors.push('Две особые клетки заняли одну позицию')
  for (const x of occupied) if (x < 1 || x > last - 1) errors.push(`Особая клетка ${x} вне игровых клеток`)
  if (plan.luck.length !== plan.swamps.length) errors.push('Клеток LUCK не по одной на болото')
  if (plan.setback.length !== plan.swamps.length) errors.push('Клеток SETBACK не по одной на болото')

  return errors
}

// ============================================================================
// 4. КОНТЕНТ: ЛОВУШКИ И ВОПРОСЫ
// ============================================================================

/** Ловушка как единица контента. Движок текстов и роликов не содержит, R103, R104. */
export interface Trap {
  id: string
  title: string
  subtitle: string
  category: string
  introText: string
  video: string | null
  explanation: string
}

export interface Answer {
  id: string
  text: string
}

/** Полный вопрос. Живёт только на сервере. */
export interface Question {
  id: string
  /** Категория: совпадает с ловушкой болота, из которого вопрос может прийти, R81. */
  trapId: string
  difficulty: 1 | 2 | 3
  ageGroup: string
  language: string
  prompt: string
  answers: Answer[]
  correctAnswerId: string
  explanation: string
}

/** Вопрос в том виде, в котором его можно отдать клиенту до раскрытия, R85. */
export type PublicQuestion = Omit<Question, 'correctAnswerId' | 'explanation'>

function toPublicQuestion(q: Question): PublicQuestion {
  const { correctAnswerId, explanation, ...rest } = q
  return rest
}

export interface Content {
  traps: Trap[]
  questions: Question[]
}

export function validateContent(content: Content, rules: BoardRules = DEFAULT_BOARD_RULES): string[] {
  const errors: string[] = []
  const need = rules.swampDepths.length

  const trapIds = new Set<string>()
  for (const trap of content.traps) {
    if (trapIds.has(trap.id)) errors.push(`Дублирующийся id ловушки: ${trap.id}`)
    trapIds.add(trap.id)
  }
  if (trapIds.size < need) errors.push(`Ловушек ${trapIds.size}, нужно минимум ${need}`)

  const questionIds = new Set<string>()
  const banks: Record<string, number> = {}
  for (const q of content.questions) {
    if (questionIds.has(q.id)) errors.push(`Дублирующийся id вопроса: ${q.id}`)
    questionIds.add(q.id)
    if (!trapIds.has(q.trapId)) errors.push(`Вопрос ${q.id} ссылается на неизвестную ловушку ${q.trapId}`)
    if (q.answers.length < 2) errors.push(`Вопрос ${q.id}: меньше двух вариантов`)
    const answerIds = new Set(q.answers.map((a) => a.id))
    if (answerIds.size !== q.answers.length) errors.push(`Вопрос ${q.id}: дублирующиеся id вариантов`)
    if (!answerIds.has(q.correctAnswerId))
      errors.push(`Вопрос ${q.id}: correctAnswerId не найден среди вариантов`)
    if (!q.explanation.trim()) errors.push(`Вопрос ${q.id}: пустое объяснение`)
    banks[q.trapId] = (banks[q.trapId] ?? 0) + 1
  }

  for (const id of trapIds) if (!banks[id]) errors.push(`У ловушки ${id} пустой банк вопросов`)
  return errors
}

// ============================================================================
// 5. СОСТОЯНИЕ
// ============================================================================

/**
 * Игрок. Позиция задана одним полем node, R92: клетка, глубина и болото
 * выводятся из него функцией positionOf.
 * Статусы ловушек, показанные вопросы и статистика здесь не хранятся —
 * они выводятся из журнала, R89-R91.
 */
export interface Player {
  id: PlayerId
  name: string
  pawn: PawnId
  /**
   * Имя рисунка аватарки. Ядро в него не смотрит и списка не знает: выбор
   * делает игрок в лобби, а рисует клиент. Пустая строка — выбора не было,
   * и клиент берёт аватарку по месту за столом.
   */
  avatar: string
  node: NodeId
  /** На каком ходу игрок финишировал. null — партия для него ещё идёт. */
  finishedTurn: number | null
  /** Есть ли живое соединение. На право хода не влияет, R98. */
  connected: boolean
  /** Может ли игрок стартовать партию. */
  isHost: boolean
}

/** Незавершённое действие текущего хода. Лежит внутри состояния ради сериализации. */
export interface Pending {
  trapId: string | null
  questionId: string | null
  /** Вопрос телепорта не меняет опыт ловушек; вид телепорта определяет исход ответа. */
  teleportKind: 'boost' | 'throw' | null
  teleportTarget: NodeId | null
  from: NodeId | null
  path: NodeId[]
  dice: number | null
}

/** Запись одного хода. Журнал — единственный источник истины об истории, R88. */
export interface TurnRecord {
  turn: number
  playerId: PlayerId
  event: TurnEvent
  dice: number | null
  /** Длительность удержания кубика в миллисекундах, R47. */
  holdMs: number | null
  from: NodeId
  to: NodeId
  /** Пройденные узлы без стартового. Клиент подсвечивает их по одному, R59. */
  path: NodeId[]
  landed: CellType | null
  trapId: string | null
  trapStatusBefore: TrapStatus | null
  trapStatusAfter: TrapStatus | null
  questionId: string | null
  answerId: string | null
  correctAnswerId: string | null
  correct: boolean | null
  explanation: string | null
  /** Название ловушки для уведомления о падении, R63. */
  trapTitle: string | null
  at: number
}

/**
 * Открытая комната в списке лобби. Живёт здесь, рядом с реестром партий, а
 * наружу уходит через протокол: раньше форма была описана дважды — типом на
 * проводе и структурой в реестре.
 */
/** Кто сидит за столом: ровно то, что нужно лобби, чтобы нарисовать фишку. */
export interface RoomSeat {
  name: string
  pawn: PawnId
  /** Как в `Player.avatar`: пустая строка — выбора не было. */
  avatar: string
}

export interface RoomSummary {
  code: string
  roomName: string
  players: number
  maxPlayers: number
  /** Занятые места в порядке входа. Зрителей тут нет: ядро о них не знает. */
  seats: RoomSeat[]
  /** Порог автозапуска, 0 — выключен: лобби показывает его меткой, менять там нечем, R116. */
  autoStartMin: number
  /** Партия уже идёт: войти игроком нельзя, смотреть можно (R112). */
  started: boolean
  /** Партия завершена; комната остаётся в списке до явного удаления. */
  finished: boolean
}

export interface Settings {
  diceSides: number
  maxPlayers: number
  /** Время на ход. Канон допускает от 20000 до 30000, R40. */
  turnMs: number
  /** Время на ответ на вопрос: вдвое больше хода, читать и выбирать дольше, чем бросать, R40. */
  answerMs: number
  /**
   * Пауза между ходами. Задумана под экран результата, которого пока нет, и
   * всё это время клиент показывал уже доехавшую фишку и погашенный кубик.
   * Держим её короткой: показ хода — сама анимация, а звать к следующему
   * броску клиент всё равно не станет, пока фишка не встанет.
   */
  resultMs: number
  /**
   * Окно итога ответа (R114): столько он стоит на экране, потом сервер сам закрывает его (`resultClosed`),
   * и у всех вместе проигрывается последствие. Игрок с ходом может закрыть раньше.
   */
  resultWindowMs: number
  /**
   * Окно уведомления о падении (R114): после показа хода оно стоит столько, потом очередь идёт дальше. Игрок
   * с ходом может закрыть раньше. Клиент показывает уведомление по окончании анимации, но срок задаёт сервер.
   */
  noticeWindowMs: number
  /**
   * Бюджет показа хода после вопроса или первого падения (кубик, шаги, падение, последствие): ритм задаёт
   * сервер, клиент лишь следует ему. Молчащий или отключившийся игрок не держит партию дольше срока.
   */
  showMs: number
  /** Во сколько раз клиент проигрывает показ хода быстрее. Единица в игре; больше — только в тестовом режиме сервера (`DURATION_ENV`). */
  speed: number
  /**
   * Отсчёт до автозапуска в лобби, R115. Короткий: к нему приходят, когда все
   * уже нажали «готов», то есть согласились, и ждать больше нечего.
   */
  autoStartMs: number
  /** Верхняя граница длительности удержания кубика. */
  maxHoldMs: number
}

/**
 * Тестовый режим сервера: переменные окружения со сроками в мс и настройки, которые они переопределяют. Задана хотя бы
 * одна — сервер сжимает сроки партии и принимает тестовые действия `harness:*`; в игре ни одной нет.
 */
export const DURATION_ENV = {
  GAME_AUTO_START_MS: 'autoStartMs',
  ANSWER_DIALOG_MS: 'answerMs',
  RESULT_DIALOG_MS: 'resultWindowMs',
  FALLER_DIALOG_MS: 'noticeWindowMs',
  MOVE_ANIMATION_MS: 'showMs',
  TURN_PAUSE_MS: 'resultMs',
} as const satisfies Record<string, keyof Settings>

export const DEFAULT_SETTINGS: Settings = {
  diceSides: 6,
  maxPlayers: 4,
  turnMs: 25_000,
  answerMs: 60_000,
  resultMs: 1_500,
  resultWindowMs: 3_000,
  noticeWindowMs: 10_000,
  showMs: 5_000,
  speed: 1,
  autoStartMs: 10_000,
  maxHoldMs: 5_000,
}

/** Полное состояние партии. Сериализуется целиком, R94. */
export interface GameState {
  gameId: GameId
  code: string
  /** Название комнаты, видимое в списке. Код остаётся для прямой ссылки. */
  roomName: string
  phase: Phase
  revision: number
  turnNumber: number
  currentPlayerId: PlayerId | null
  phaseEndsAt: number | null
  /**
   * Результат ответа закрыт активным игроком (или вышло его время): все клиенты в этот момент
   * убирают окно и вместе проигрывают последствие. Сбрасывается с каждой сменой фазы, R114.
   */
  resultClosed: boolean
  /** Когда сервер сам закроет итог ответа; `null` вне итога и после закрытия. R114. */
  resultEndsAt: number | null
  /**
   * Порог автозапуска, R115, R116: 0 — выключен, иначе число игроков на связи, с
   * которого идёт отсчёт. Настройка комнаты: её меняет хозяин, и лежит она в
   * состоянии, а не у клиента, чтобы все видели одно и то же.
   */
  autoStartMin: number
  /** Когда партия стартует сама. null — отсчёт не идёт, R115. */
  startsAt: number | null
  boardPlan: BoardPlan
  settings: Settings
  order: PlayerId[]
  players: Record<PlayerId, Player>
  pending: Pending | null
  /** Журнал ходов. Из него выводится всё, что зависит от истории. */
  turns: TurnRecord[]
  winnerId: PlayerId | null
  /** Токены сессии для возврата после обрыва, R100, R101. */
  tokens: Record<string, PlayerId>
  appliedCommands: Record<PlayerId, string[]>
  seed: number
  rngCursor: number
  createdAt: number
  updatedAt: number
}

// ============================================================================
// 6. ВЫВОДИМОЕ ИЗ ЖУРНАЛА
// ============================================================================

/** Статус ловушки в партии, R89: один на всех игроков. Ничего не хранится, всё считается по журналу. */
export function trapStatusOf(turns: TurnRecord[], trapId: string): TrapStatus {
  let status: TrapStatus = TrapStatus.UNSEEN
  for (const turn of turns) {
    if (turn.trapId !== trapId) continue
    if (turn.trapStatusAfter === TrapStatus.MASTERED) return TrapStatus.MASTERED
    if (turn.trapStatusAfter === TrapStatus.SEEN) status = TrapStatus.SEEN
  }
  return status
}

/** Показанные вопросы всей партии, R83, R90. Список общий, а не по игрокам. */
export function usedQuestionIds(turns: TurnRecord[]): string[] {
  const out: string[] = []
  for (const turn of turns) if (turn.questionId && !out.includes(turn.questionId)) out.push(turn.questionId)
  return out
}

/** Статистика игрока, R91. Её место здесь, а не в состоянии ловушки. */
export function playerStatsOf(
  turns: TurnRecord[],
  playerId: PlayerId,
): { encounters: number; failedTests: number; passedTests: number; timeouts: number } {
  const stats = { encounters: 0, failedTests: 0, passedTests: 0, timeouts: 0 }
  for (const turn of turns) {
    if (turn.playerId !== playerId) continue
    if (turn.event === TurnEvent.TRAP_FIRST || turn.event === TurnEvent.TRAP_MASTERED) stats.encounters++
    if (turn.event === TurnEvent.ANSWER_WIN) stats.passedTests++
    if (turn.event === TurnEvent.ANSWER_FAIL) stats.failedTests++
    if (turn.event === TurnEvent.ROLL_TIMEOUT || turn.event === TurnEvent.ANSWER_TIMEOUT) stats.timeouts++
  }
  return stats
}

/** Кто стоит на клетке. Ограничения нет, допускаются все четыре фишки, R33. */
export function pawnsAt(state: GameState, cellIndex: number, depth = 0): PlayerId[] {
  return state.order.filter((id) => {
    const player = state.players[id]
    if (!player) return false
    const pos = positionOf(player.node)
    return pos.cellIndex === cellIndex && pos.depth === depth
  })
}

// ============================================================================
// 7. ПРОТОКОЛ
// ============================================================================

export interface BaseCommand {
  commandId: string
}

export interface JoinGameCommand extends BaseCommand {
  type: 'joinGame'
  name: string
  pawn?: PawnId
  /** Выбранная аватарка. Ядро её только хранит, см. `Player.avatar`. */
  avatar?: string
  /** Секретный ключ возврата в свой слот после обрыва, R101. */
  sessionToken?: string
  /**
   * Токен для нового слота. Ставит только транспорт, из криптостойкого источника;
   * из клиентского действия сюда ничего не попадает, иначе клиент выбрал бы себе
   * чужой токен и занял бы соседний слот. Без него ядро падает на свой
   * детерминированный генератор — так работают прямые тесты ядра.
   */
  issuedToken?: string
}
export interface StartGameCommand extends BaseCommand {
  type: 'startGame'
}
/**
 * Порог автозапуска комнаты: 0 — выключить, иначе от 2 до `maxPlayers`. Только
 * хозяин, только в лобби, R116.
 */
export interface SetAutoStartCommand extends BaseCommand {
  type: 'setAutoStart'
  min: number
}
export interface RollDiceCommand extends BaseCommand {
  type: 'rollDice'
  turn: number
  /** Сколько миллисекунд игрок удерживал кубик перед отпусканием, R46, R48. */
  holdMs: number
}
export interface SubmitAnswerCommand extends BaseCommand {
  type: 'submitAnswer'
  turn: number
  questionId: string
  answerId: string
}
/** Подтверждение показа результата: очередь идёт дальше, не дожидаясь срока, R114. */
export interface AcknowledgeCommand extends BaseCommand {
  type: 'acknowledge'
  turn: number
}
/** Окно результата ответа закрыто: все клиенты вместе проигрывают последствие, R114. */
export interface CloseResultCommand extends BaseCommand {
  type: 'closeResult'
  turn: number
}
export interface LeaveGameCommand extends BaseCommand {
  type: 'leaveGame'
}

export type Command =
  | JoinGameCommand
  | StartGameCommand
  | SetAutoStartCommand
  | RollDiceCommand
  | SubmitAnswerCommand
  | AcknowledgeCommand
  | CloseResultCommand
  | LeaveGameCommand

/** Снапшот: один на всех. Единственный способ, которым состояние покидает сервер. */
export interface Snapshot {
  gameId: GameId
  code: string
  roomName: string
  phase: Phase
  revision: number
  serverNow: number
  turnNumber: number
  currentPlayerId: PlayerId | null
  phaseEndsAt: number | null
  /** Окно результата ответа закрыто: пора проигрывать последствие. Только в `TURN_RESULT`, R114. */
  resultClosed: boolean
  /** Когда сервер сам закроет итог ответа (часы сервера, как `phaseEndsAt`); `null` вне итога. R114. */
  resultEndsAt: number | null
  autoStartMin: number
  startsAt: number | null
  board: Board
  settings: Settings
  players: Player[]
  winnerId: PlayerId | null
  /** Таблица мест: дальше по линии лучше, глубина в болоте хуже. Победитель стоит на финише, поэтому первый. */
  standings: PlayerId[]
  /** Вопрос без правильного ответа. Виден всем, пока идёт ответ, отвечает только активный игрок, R86. */
  activeQuestion: PublicQuestion | null
  /** Путь до клетки вопроса: клиент проигрывает его до показа диалога. */
  pendingMove: { from: NodeId; path: NodeId[]; dice: number | null } | null
  /** Последние ходы. Клиент по ним доигрывает пропущенное после возврата. */
  recentTurns: TurnRecord[]
}

export type ServerEvent =
  | { type: 'joined'; playerId: PlayerId; sessionToken: string; snapshot: Snapshot }
  | { type: 'snapshot'; snapshot: Snapshot }
  | { type: 'turnStarted'; turn: number; playerId: PlayerId; phase: Phase; phaseEndsAt: number | null }
  | { type: 'turnResolved'; record: TurnRecord }
  | { type: 'gameFinished'; winnerId: PlayerId | null; standings: PlayerId[] }

export const RejectCode = {
  BAD_PROTOCOL: 'bad_protocol',
  GAME_NOT_FOUND: 'game_not_found',
  GAME_FULL: 'game_full',
  NOT_IN_GAME: 'not_in_game',
  NOT_HOST: 'not_host',
  NOT_YOUR_TURN: 'not_your_turn',
  WRONG_PHASE: 'wrong_phase',
  WRONG_TURN: 'wrong_turn',
  INVALID_PAYLOAD: 'invalid_payload',
  ALREADY_STARTED: 'already_started',
  NOT_ENOUGH_PLAYERS: 'not_enough_players',
  /** Цвет фишки уже у другого игрока комнаты, R117. */
  PAWN_TAKEN: 'pawn_taken',
  /** Аватарка уже у другого игрока комнаты, R117. */
  AVATAR_TAKEN: 'avatar_taken',
  /** Имя уже носит другой игрок комнаты, в том числе отключившийся: его слот ещё его, R122. */
  NAME_TAKEN: 'name_taken',
  /** Ниже — отказы транспорта: до ядра действие не дошло. Список общий, чтобы клиент знал один. */
  BAD_ACTION: 'bad_action',
  NOT_JOINED: 'not_joined',
  ROOMS_FULL: 'rooms_full',
} as const
export type RejectCode = (typeof RejectCode)[keyof typeof RejectCode]

export interface Outgoing {
  to: PlayerId | '*'
  event: ServerEvent
}

export type CommandResult = { ok: true } | { ok: false; code: RejectCode; reason: string }

export const reject = (code: RejectCode, reason: string): CommandResult => ({ ok: false, code, reason })

// ============================================================================
// 8. ДВИЖОК
// ============================================================================

export interface GameOptions {
  content: Content
  /** Название комнаты, его вводит создатель. Пусто — берётся код. */
  roomName?: string
  rules?: BoardRules
  boardPlan?: BoardPlan
  settings?: Partial<Settings>
  gameId?: GameId
  code?: string
  seed?: number
  now?: number
  /** Порог автозапуска при создании. По умолчанию 0 — выключен, R116. */
  autoStartMin?: number
}

/**
 * Единственный владелец правды о партии.
 *
 *   const game = new Game({ content });
 *   game.handle(playerId, command, Date.now());
 *   for (const out of game.drain()) transport.send(out);
 *   game.tick(Date.now()); // на интервале ~250 мс
 */
export class Game {
  /** Публично и намеренно: это весь снимок партии, его можно сохранить и восстановить. */
  state: GameState

  private board: Board
  private trapsById: Record<string, Trap>
  private questionsById: Record<string, Question>
  private banks: Record<string, string[]>
  private allQuestionIds: string[]
  private outbox: Outgoing[] = []
  /** Часы остановлены проверкой с этого момента (`harness:hold`); `null` — идут. */
  private heldAt: number | null = null
  /** Подсказка кубику для сквозных проверок: следующий бросок даст это число (`harness:dice`). В снимок не входит. */
  private rigged: number | null = null

  constructor(opts: GameOptions) {
    const rules = opts.rules ?? DEFAULT_BOARD_RULES
    const contentErrors = validateContent(opts.content, rules)
    if (contentErrors.length) throw new Error(`Невалидный контент: ${contentErrors.join('; ')}`)

    const now = opts.now ?? Date.now()
    const seed = opts.seed ?? (Math.random() * 2 ** 32) >>> 0
    const plan =
      opts.boardPlan ??
      generateBoardPlan(
        seed,
        opts.content.traps.map((t) => t.id),
        rules,
      )
    const planErrors = validateBoardPlan(plan, rules)
    if (planErrors.length) throw new Error(`Невалидное поле: ${planErrors.join('; ')}`)

    this.board = createBoard(plan)
    this.trapsById = Object.fromEntries(opts.content.traps.map((t) => [t.id, t]))
    this.questionsById = Object.fromEntries(opts.content.questions.map((q) => [q.id, q]))
    this.banks = {}
    this.allQuestionIds = opts.content.questions.map((q) => q.id)
    for (const q of opts.content.questions) (this.banks[q.trapId] ??= []).push(q.id)

    this.state = {
      gameId: opts.gameId ?? `g_${seed.toString(36)}`,
      code: opts.code ?? generateCode(seed),
      roomName: cleanText(opts.roomName).slice(0, ROOM_NAME_MAX).trim() || (opts.code ?? generateCode(seed)),
      phase: Phase.LOBBY,
      revision: 0,
      turnNumber: 0,
      currentPlayerId: null,
      phaseEndsAt: null,
      resultClosed: false,
      resultEndsAt: null,
      autoStartMin: opts.autoStartMin ?? 0,
      startsAt: null,
      boardPlan: plan,
      settings: { ...DEFAULT_SETTINGS, ...opts.settings },
      order: [],
      players: {},
      pending: null,
      turns: [],
      winnerId: null,
      tokens: {},
      appliedCommands: {},
      seed,
      rngCursor: 0,
      createdAt: now,
      updatedAt: now,
    }
  }

  /** Поднять партию из сохранённого состояния. Контент приходит извне, R103. */
  static restore(state: GameState, content: Content): Game {
    const game = new Game({ content, boardPlan: state.boardPlan, seed: state.seed, now: state.createdAt })
    game.state = state
    game.board = createBoard(state.boardPlan)
    return game
  }

  get id(): GameId {
    return this.state.gameId
  }
  get code(): string {
    return this.state.code
  }
  get phase(): Phase {
    return this.state.phase
  }
  getBoard(): Board {
    return this.board
  }

  /** Забрать накопившиеся исходящие события. Вызывать после каждого handle и tick. */
  drain(): Outgoing[] {
    const out = this.outbox
    this.outbox = []
    return out
  }

  // --- случайность ----------------------------------------------------------

  private random(): number {
    return mulberry32(this.state.seed + this.state.rngCursor++ * GOLDEN)
  }

  /**
   * Бросок кубика из жеста, R47-R49.
   * Длительность удержания подмешивается в ГСЧ, поэтому жест действительно
   * влияет на результат, но распределение остаётся равномерным:
   * связь между длительностью и гранью — хеш, прицелиться в неё нельзя.
   */
  /** Следующий бросок даст `value` (1..diceSides) вместо случайного; `null` снимает подсказку. */
  rigDice(value: number | null): boolean {
    if (value !== null && !(Number.isInteger(value) && value >= 1 && value <= this.state.settings.diceSides))
      return false
    this.rigged = value
    return true
  }

  /**
   * Сократить срок текущей фазы до `ms` от `now` (`harness:deadline`, только для сквозных проверок): тесты
   * молчания не ждут настоящих секунд, а срок по-прежнему кончает сервер своими часами.
   */
  rushPhase(ms: number, now: number = Date.now()): boolean {
    now = this.clockAt(now)
    const end = this.state.phaseEndsAt
    if (end === null || !(ms >= 0)) return false
    this.state.phaseEndsAt = Math.min(end, now + ms)
    // Нулевой срок кончается сразу и при остановленных часах: проверка получает окно, которого дальше ждёт сама.
    if (ms === 0) this.advance(now)
    else {
      this.bump(now)
      this.broadcastSnapshot(now)
    }
    return true
  }

  /**
   * Остановить часы комнаты или пустить их дальше (`harness:hold`, только для сквозных проверок): пока они стоят,
   * сервер не двигает сроки, а при возобновлении сдвигает их на время простоя. Так окно, которое снимают на
   * эталон, не закроется само посреди снимка, хоть тот и идёт секундами.
   */
  hold(on: boolean, now: number = Date.now()): void {
    if (on === (this.heldAt !== null)) return
    if (on) {
      this.heldAt = now
      return
    }
    const gap = now - this.heldAt!
    this.heldAt = null
    const { phaseEndsAt, resultEndsAt, startsAt } = this.state
    if (phaseEndsAt !== null) this.state.phaseEndsAt = phaseEndsAt + gap
    if (resultEndsAt !== null) this.state.resultEndsAt = resultEndsAt + gap
    if (startsAt !== null) this.state.startsAt = startsAt + gap
    this.bump(now)
    this.broadcastSnapshot(now)
  }

  /**
   * Время по часам комнаты: пока они стоят (`hold`), всё, что считается от «сейчас» (сроки, `serverNow`), идёт от
   * мгновения остановки. Тогда сдвиг сроков на время простоя при возобновлении верен и для того, что поставлено
   * уже во время простоя: закрытое рукой окно не получает лишних секунд простоя.
   */
  private clockAt(now: number): number {
    return this.heldAt ?? now
  }

  private rollFromHold(holdMs: number): number {
    const clamped = Math.max(0, Math.min(this.state.settings.maxHoldMs, Math.round(holdMs)))
    const mixed = mulberry32(
      this.state.seed + this.state.rngCursor++ * GOLDEN + Math.imul(clamped + 1, 0x85ebca6b),
    )
    const random = 1 + Math.floor(mixed * this.state.settings.diceSides)
    // Генератор продвигается и под подсказкой: остальные броски не зависят от того, подсказывали ли этот.
    const rigged = this.rigged
    this.rigged = null
    return rigged ?? random
  }

  // --- маршрутизация --------------------------------------------------------

  /** Единственный вход для команд. playerId берётся из серверной сессии, не из тела команды. */
  handle(playerId: PlayerId, command: Command, now: number = Date.now()): CommandResult {
    now = this.clockAt(now)
    // Команда приходит из сети: не объект или без строковых type и commandId — не команда.
    if (
      !command ||
      typeof command !== 'object' ||
      typeof command.type !== 'string' ||
      (command.commandId !== undefined && typeof command.commandId !== 'string')
    ) {
      return reject(RejectCode.BAD_PROTOCOL, 'Команда имеет неверную форму')
    }

    if (this.state.appliedCommands[playerId]?.includes(command.commandId)) return { ok: true }

    // Дедлайн проверяется и здесь, а не только фоновым таймером: между тиками
    // проходит до tickMs, и команда может прийти уже после дедлайна. Без этого
    // просроченный ход был бы принят. Заодно модуль остаётся корректным
    // вообще без внешнего таймера — это нужно тестам и любому транспорту.
    this.tick(now)

    let result: CommandResult
    switch (command.type) {
      case 'joinGame':
        result = this.cmdJoin(playerId, command, now)
        break
      case 'startGame':
        result = this.cmdStart(playerId, now)
        break
      case 'setAutoStart':
        result = this.cmdSetAutoStart(playerId, command, now)
        break
      case 'rollDice':
        result = this.cmdRoll(playerId, command, now)
        break
      case 'submitAnswer':
        result = this.cmdAnswer(playerId, command, now)
        break
      case 'acknowledge':
        result = this.cmdAcknowledge(playerId, command, now)
        break
      case 'closeResult':
        result = this.cmdCloseResult(playerId, command, now)
        break
      case 'leaveGame':
        result = this.cmdLeave(playerId, now)
        break
      default:
        result = reject(RejectCode.INVALID_PAYLOAD, 'Неизвестная команда')
    }

    if (result.ok) {
      this.remember(playerId, command.commandId)
      this.state.updatedAt = now
    }
    return result
  }

  /**
   * Двигает дедлайны. Вызывается на интервале ~250 мс и перед разбором каждой команды.
   * Сервер никогда не бросает кубик за игрока, R44: он лишь закрывает ход.
   */
  tick(now: number = Date.now()): void {
    // Часы остановлены проверкой (`harness:hold`): сроки стоят, пока она снимает кадры и сверяет окна.
    if (this.heldAt === null) this.advance(now)
  }

  private advance(now: number): void {
    // Лобби живёт своим сроком: у него нет фазового дедлайна, зато есть автозапуск, R115.
    if (this.state.phase === Phase.LOBBY) {
      if (this.state.startsAt !== null && now >= this.state.startsAt && this.autoStartHolds())
        this.startNow(now)
      return
    }
    // Итог ответа закрывает сервер по своим часам: у всех вместе стартует последствие, R114.
    if (this.state.resultEndsAt !== null && now >= this.state.resultEndsAt) this.closeResult(now)
    if (this.state.phaseEndsAt === null || now < this.state.phaseEndsAt) return
    const player = this.activePlayer()

    switch (this.state.phase) {
      case Phase.AWAIT_ROLL:
        // Не бросил: фишка остаётся на месте, очередь идёт дальше, R41.
        if (player) {
          this.record(
            {
              playerId: player.id,
              event: TurnEvent.ROLL_TIMEOUT,
              dice: null,
              holdMs: null,
              from: player.node,
              to: player.node,
              path: [],
              landed: null,
            },
            now,
          )
          this.endTurn(now)
        }
        break
      case Phase.AWAIT_ANSWER:
        // Не ответил: падает на дно болота, как при первом попадании, R42, R78.
        if (player) this.closeQuestion(player, null, true, now)
        break
      case Phase.TURN_RESULT:
        this.nextTurn(now)
        break
      default:
        break
    }
  }

  // --- команды лобби --------------------------------------------------------

  private cmdJoin(playerId: PlayerId, cmd: JoinGameCommand, now: number): CommandResult {
    if (cmd.sessionToken) {
      const ownerId = this.state.tokens[cmd.sessionToken]
      const existing = ownerId ? this.state.players[ownerId] : undefined
      if (existing) {
        // R108: слот не переезжает на другой playerId. Вызывающий обязан прийти
        // с владельцем токена — его отдаёт playerOfToken. Иначе это попытка
        // увести чужой слот на своё соединение, и ей тут не место.
        if (ownerId !== playerId) return reject(RejectCode.BAD_PROTOCOL, 'Токен принадлежит другому слоту')
        existing.connected = true
        // Права хозяина обратно не возвращаются (R107), но пустое место надо
        // занять: иначе комната, из которой все вышли, остаётся без хозяина и
        // стартовать её больше некому.
        if (!this.hasHost()) existing.isHost = true
        return this.welcome(playerId, cmd.sessionToken, now)
      }
    }

    // Слот уже занят этим игроком: второй вход без токена не заводит двойника в order, R122.
    if (this.state.players[playerId]) return reject(RejectCode.BAD_PROTOCOL, 'Вы уже в партии')
    if (this.state.phase !== Phase.LOBBY) return reject(RejectCode.ALREADY_STARTED, 'Партия уже началась')
    if (this.state.order.length >= this.state.settings.maxPlayers)
      return reject(RejectCode.GAME_FULL, 'Мест нет')
    const name = cleanText(cmd.name)
    if (name.length < 1 || name.length > NAME_MAX)
      return reject(RejectCode.INVALID_PAYLOAD, `Имя от одного до ${NAME_MAX} символов`)

    // R117: цвет и аватарка у каждого игрока комнаты свои, и занятое не подменяется
    // молча — человек должен узнать, что выбрал не то, что получил. Пустая
    // аватарка — «выбора не было», по ней уникальность не проверяется.
    const avatar = cmd.avatar ?? ''
    if (avatar !== '' && (typeof avatar !== 'string' || !AVATAR_PATTERN.test(avatar)))
      return reject(RejectCode.INVALID_PAYLOAD, 'Недопустимая аватарка')
    if (cmd.pawn !== undefined && !PAWNS.some((p) => p.id === cmd.pawn))
      return reject(RejectCode.INVALID_PAYLOAD, 'Нет такого цвета')
    const others = this.playerList()
    if (others.some((p) => p.name.trim().toLowerCase() === name.toLowerCase()))
      return reject(RejectCode.NAME_TAKEN, 'Это имя уже занято')
    if (cmd.pawn && others.some((p) => p.pawn === cmd.pawn))
      return reject(RejectCode.PAWN_TAKEN, 'Этот цвет уже занят')
    if (avatar && others.some((p) => p.avatar === avatar))
      return reject(RejectCode.AVATAR_TAKEN, 'Эта аватарка уже занята')

    this.state.players[playerId] = {
      id: playerId,
      name,
      pawn: this.pickPawn(cmd.pawn),
      avatar,
      node: this.board.startId,
      finishedTurn: null,
      connected: true,
      isHost: !this.hasHost(),
    }
    this.state.order.push(playerId)

    const token = cmd.issuedToken ?? generateToken(this.state.seed + this.state.order.length, this.random())
    this.state.tokens[token] = playerId

    return this.welcome(playerId, token, now)
  }

  private cmdStart(playerId: PlayerId, now: number): CommandResult {
    const player = this.state.players[playerId]
    if (!player) return reject(RejectCode.NOT_IN_GAME, 'Вы не в партии')
    if (!player.isHost) return reject(RejectCode.NOT_HOST, 'Стартует только хозяин')
    if (this.state.phase !== Phase.LOBBY) return reject(RejectCode.WRONG_PHASE, 'Партия уже идёт')
    if (this.state.order.length < 1) return reject(RejectCode.NOT_ENOUGH_PLAYERS, 'Нужен хотя бы один игрок')
    this.startNow(now)
    return { ok: true }
  }

  /** Хозяин ставит порог автозапуска комнаты, R116. Порог выше числа игроков гасит идущий отсчёт. */
  private cmdSetAutoStart(playerId: PlayerId, cmd: SetAutoStartCommand, now: number): CommandResult {
    const player = this.state.players[playerId]
    if (!player) return reject(RejectCode.NOT_IN_GAME, 'Вы не в партии')
    if (!player.isHost) return reject(RejectCode.NOT_HOST, 'Настройки меняет только хозяин')
    if (this.state.phase !== Phase.LOBBY) return reject(RejectCode.WRONG_PHASE, 'Партия уже идёт')
    const max = this.state.settings.maxPlayers
    const ok = Number.isInteger(cmd.min) && (cmd.min === 0 || (cmd.min >= 2 && cmd.min <= max))
    if (!ok) return reject(RejectCode.INVALID_PAYLOAD, `Порог: 0 или от 2 до ${max}`)
    this.state.autoStartMin = cmd.min
    this.bump(now)
    this.broadcastSnapshot(now)
    return { ok: true }
  }

  /** Старт партии, один на ручной запуск и автозапуск. */
  private startNow(now: number): void {
    this.state.startsAt = null
    this.state.turnNumber = 0
    this.state.currentPlayerId = this.state.order[0]
    this.beginTurn(now)
  }

  /**
   * Можно ли сейчас вести отсчёт до автозапуска, R115: порог задан, и на связи
   * не меньше игроков, чем он требует. «Готов» — это присутствие в комнате, а
   * отдельной отметки нет: отключившихся не считают, их слот сохранён, но
   * держать из-за них комнату нельзя.
   */
  private autoStartHolds(): boolean {
    const min = this.state.autoStartMin
    if (min < 2 || this.state.phase !== Phase.LOBBY) return false
    return this.playerList().filter((p) => p.connected).length >= min
  }

  /**
   * Приводит отсчёт к условию: условие появилось — отсчёт начинается, пропало —
   * гаснет, держится — идёт дальше и не перезапускается. Зовётся перед каждой
   * рассылкой снимка, поэтому любое изменение лобби — вход, выход, обрыв,
   * порог — проходит через одно место.
   */
  private syncAutoStart(now: number): void {
    if (!this.autoStartHolds()) this.state.startsAt = null
    else if (this.state.startsAt === null) this.state.startsAt = now + this.state.settings.autoStartMs
  }

  private cmdLeave(playerId: PlayerId, now: number): CommandResult {
    const player = this.state.players[playerId]
    if (!player) return reject(RejectCode.NOT_IN_GAME, 'Вы не в партии')

    // Осознанный выход и обрыв — разные вещи (R111). Обрыв слот бережёт (R99,
    // R107), потому что человек собирается вернуться. Выход место освобождает,
    // Пустая комната остаётся доступной для следующего игрока (R119).
    if (this.state.phase === Phase.LOBBY) {
      const wasHost = player.isHost
      delete this.state.players[playerId]
      this.state.order = this.state.order.filter((id) => id !== playerId)
      delete this.state.appliedCommands[playerId]
      for (const [token, owner] of Object.entries(this.state.tokens))
        if (owner === playerId) delete this.state.tokens[token]
      if (wasHost) this.passHost()
      this.bump(now)
      this.broadcastSnapshot(now)
      return { ok: true }
    }

    this.disconnect(playerId, now)
    return { ok: true }
  }

  /** Обрыв связи партию не останавливает, слот сохраняется до конца, R98, R99. */
  /**
   * Чей слот открывает токен. Нужно транспорту: он обязан звать handle уже с
   * постоянным playerId, а не заводить игрока заново на каждом соединении.
   */
  playerOfToken(token: string): PlayerId | undefined {
    const owner = this.state.tokens[token]
    return owner && this.state.players[owner] ? owner : undefined
  }

  disconnect(playerId: PlayerId, now: number = Date.now()): void {
    now = this.clockAt(now)
    const player = this.state.players[playerId]
    if (!player) return
    player.connected = false

    // Слот не освобождается и в лобби (R99, R107): иначе обрыв до старта стоил
    // бы игроку места, а его токен указывал бы на удалённый слот.
    if (player.isHost) {
      player.isHost = false
      this.passHost()
    }
    this.bump(now)
    this.broadcastSnapshot(now)
  }

  // --- команды хода ---------------------------------------------------------

  private guardTurn(
    playerId: PlayerId,
    cmd: BaseCommand & { turn: number },
    phase: Phase,
  ): CommandResult | Player {
    const player = this.state.players[playerId]
    if (!player) return reject(RejectCode.NOT_IN_GAME, 'Вы не в партии')
    if (this.state.phase !== phase) return reject(RejectCode.WRONG_PHASE, 'Сейчас другая фаза')
    if (this.state.currentPlayerId !== playerId) return reject(RejectCode.NOT_YOUR_TURN, 'Сейчас не ваш ход')
    if (cmd.turn !== this.state.turnNumber) return reject(RejectCode.WRONG_TURN, 'Ход уже сменился')
    return player
  }

  private cmdRoll(playerId: PlayerId, cmd: RollDiceCommand, now: number): CommandResult {
    const guard = this.guardTurn(playerId, cmd, Phase.AWAIT_ROLL)
    if ('ok' in guard) return guard
    if (!Number.isFinite(cmd.holdMs) || cmd.holdMs < 0)
      return reject(RejectCode.INVALID_PAYLOAD, 'Длительность удержания задана неверно')
    this.applyRoll(guard, this.rollFromHold(cmd.holdMs), cmd.holdMs, now)
    return { ok: true }
  }

  private cmdAnswer(playerId: PlayerId, cmd: SubmitAnswerCommand, now: number): CommandResult {
    const guard = this.guardTurn(playerId, cmd, Phase.AWAIT_ANSWER)
    if ('ok' in guard) return guard
    const pending = this.state.pending
    if (!pending?.questionId) return reject(RejectCode.WRONG_PHASE, 'Вопрос не задан')
    if (pending.questionId !== cmd.questionId)
      return reject(RejectCode.INVALID_PAYLOAD, 'Отвечаете не на свой вопрос')
    const question = this.questionsById[pending.questionId]
    if (!question || !question.answers.some((a) => a.id === cmd.answerId))
      return reject(RejectCode.INVALID_PAYLOAD, 'Такого варианта нет')
    this.closeQuestion(guard, cmd.answerId, false, now)
    return { ok: true }
  }

  /**
   * Клиент завершил показ результата хода, R114. Принимается только от того, чей
   * это ход, и только пока идёт показ результата: опоздавшее подтверждение
   * прошлого хода отбивается по номеру и не съедает паузу следующего.
   */
  private cmdAcknowledge(playerId: PlayerId, cmd: AcknowledgeCommand, now: number): CommandResult {
    const guard = this.guardTurn(playerId, cmd, Phase.TURN_RESULT)
    if ('ok' in guard) return guard
    this.nextTurn(now)
    return { ok: true }
  }

  /**
   * Закрыть окно результата ответа. Закрывает активный игрок, но видят все: флаг уходит в
   * снапшоты, и клиенты убирают окно и проигрывают последствие вместе, R114.
   */
  private cmdCloseResult(playerId: PlayerId, cmd: CloseResultCommand, now: number): CommandResult {
    const guard = this.guardTurn(playerId, cmd, Phase.TURN_RESULT)
    if ('ok' in guard) return guard
    const last = this.state.turns[this.state.turns.length - 1]
    const answered =
      last?.event === TurnEvent.ANSWER_WIN ||
      last?.event === TurnEvent.ANSWER_FAIL ||
      last?.event === TurnEvent.ANSWER_TIMEOUT
    if (!answered) return reject(RejectCode.WRONG_PHASE, 'Закрывать нечего')
    this.closeResult(now)
    return { ok: true }
  }

  /** Закрыть итог ответа: флаг уходит всем, а на показ последствия остаётся `showMs` от этого момента. */
  private closeResult(now: number): void {
    if (this.state.resultClosed) return
    this.state.resultClosed = true
    this.state.resultEndsAt = null
    this.state.phaseEndsAt = Math.min(this.state.phaseEndsAt ?? Infinity, now + this.state.settings.showMs)
    this.bump(now)
    this.broadcastSnapshot(now)
  }

  // --- жизненный цикл хода --------------------------------------------------

  private beginTurn(now: number): void {
    const player = this.activePlayer()
    if (!player) return
    this.state.turnNumber++
    this.state.pending = {
      trapId: null,
      questionId: null,
      teleportKind: null,
      teleportTarget: null,
      from: null,
      path: [],
      dice: null,
    }
    this.setPhase(Phase.AWAIT_ROLL, this.state.settings.turnMs, now)
    this.broadcast({
      type: 'turnStarted',
      turn: this.state.turnNumber,
      playerId: player.id,
      phase: this.state.phase,
      phaseEndsAt: this.state.phaseEndsAt,
    })
    this.broadcastSnapshot(now)
  }

  /** Бросок: движение по болоту вверх либо по основной линии вперёд. */
  private applyRoll(player: Player, dice: number, holdMs: number, now: number): void {
    const from = player.node
    const pos = positionOf(from)
    const path: NodeId[] = []

    if (pos.depth > 0) {
      const remaining = pos.depth
      if (dice < remaining) {
        /*
         * Не хватило: поднимаемся на выпавшее число, R67. Клетку остановки
         * при этом разбираем как любую другую — в колонке колодца стоят свои
         * мечи и клевер, и раньше они молчали: фишка вставала на стрелку и
         * никуда не уезжала.
         */
        for (let i = 1; i <= dice; i++) path.push(swampNodeId(pos.cellIndex, remaining - i))
        player.node = swampNodeId(pos.cellIndex, remaining - dice)
        this.resolveLanding(player, dice, holdMs, from, path, TurnEvent.SWAMP_CLIMB, now)
        return
      }
      // Хватило пройти вход: выходим и тратим остаток вперёд, R68.
      for (let i = 1; i < remaining; i++) path.push(swampNodeId(pos.cellIndex, remaining - i))
      path.push(mainNodeId(pos.cellIndex))
      let index = pos.cellIndex
      const carry = dice - remaining
      if (carry > 0) {
        const walk = this.walk(index, carry)
        path.push(...walk.path)
        index = walk.index
      }
      player.node = mainNodeId(index)
      this.resolveLanding(player, dice, holdMs, from, path, TurnEvent.SWAMP_EXIT, now)
      return
    }

    const walk = this.walk(pos.cellIndex, dice)
    path.push(...walk.path)
    player.node = mainNodeId(walk.index)
    this.resolveLanding(player, dice, holdMs, from, path, TurnEvent.NONE, now)
  }

  /**
   * Проход по основной линии с отражением от финиша, R52-R55.
   * Возвращает конечный индекс и список пройденных узлов для подсветки.
   */
  /*
   * Куда уводит перенос с этой клетки. Ответ берём из графа поля, а не считаем
   * заново: правила «только нейтральная клетка» и «одна клетка принимает один
   * перенос» разрешаются разом на всё поле, в рантайме их не воспроизвести.
   * Длина рывка и отката там зашита по настройкам по умолчанию.
   */
  private teleportTarget(nodeId: NodeId, kind: 'boost' | 'throw'): NodeId | null {
    const edge = this.board.edges.find((e) => e.from === nodeId && e.kind === kind)
    if (!edge) return null
    return this.board.nodes.some((n) => n.id === edge.to) ? edge.to : null
  }

  private walk(from: number, steps: number): { index: number; path: NodeId[] } {
    const last = this.board.mainPath.length - 1
    let index = from
    let dir = 1
    const path: NodeId[] = []
    for (let i = 0; i < steps; i++) {
      if (index === last && dir === 1) dir = -1
      if (index === 0 && dir === -1) dir = 1
      index += dir
      path.push(mainNodeId(index))
    }
    return { index, path }
  }

  /** Активирует клетку остановки; телепорты завершают ход своим вопросом. */
  private resolveLanding(
    player: Player,
    dice: number | null,
    holdMs: number | null,
    from: NodeId,
    path: NodeId[],
    baseEvent: TurnEvent,
    now: number,
  ): void {
    /*
     * Клетку берём по идентификатору, а не по номеру колонки: остановиться
     * можно и в колодце, а там у колонки свои клетки со своими типами.
     */
    const node = this.board.nodes.find((n) => n.id === player.node)
    if (!node) return

    switch (node.type) {
      case CellType.SUCCESS: {
        player.finishedTurn = this.state.turnNumber
        this.state.winnerId = player.id
        this.record(
          {
            playerId: player.id,
            event: TurnEvent.FINISH,
            dice,
            holdMs,
            from,
            to: player.node,
            path,
            landed: node.type,
          },
          now,
        )
        this.finish(now)
        return
      }
      case CellType.LUCK:
      case CellType.SETBACK: {
        const kind = node.type === CellType.LUCK ? 'boost' : 'throw'
        const landing = this.teleportTarget(player.node, kind)
        if (!landing) break
        // Любой телепорт требует ответа. Направление переноса применится после
        // ответа: клевер — за верный, драка — за неверный (или тайм-аут).
        this.state.pending = {
          trapId: null,
          questionId: this.drawQuestion(null),
          teleportKind: kind,
          teleportTarget: landing,
          from,
          path,
          dice,
        }
        this.setPhase(Phase.AWAIT_ANSWER, this.state.settings.answerMs, now)
        this.broadcastSnapshot(now)
        return
      }
      case CellType.TRAP: {
        this.resolveTrap(player, dice, holdMs, from, path, now)
        return
      }
      default:
        break
    }

    this.record(
      { playerId: player.id, event: baseEvent, dice, holdMs, from, to: player.node, path, landed: node.type },
      now,
    )
    this.endTurn(now)
  }

  /** Ловушка по статусу в партии: знакомство, вопрос или ничего, R62-R72. */
  private resolveTrap(
    player: Player,
    dice: number | null,
    holdMs: number | null,
    from: NodeId,
    path: NodeId[],
    now: number,
  ): void {
    const index = positionOf(player.node).cellIndex
    const swamp = this.board.swamps.find((s) => s.entry === index)!
    const before = trapStatusOf(this.state.turns, swamp.trapId)

    if (before === TrapStatus.MASTERED) {
      // Опыт узнаёт ловушку вовремя, падения нет, R71.
      this.recordTrap(player, now, {
        event: TurnEvent.TRAP_MASTERED,
        dice,
        holdMs,
        from,
        path,
        trapId: swamp.trapId,
        before,
        after: before,
      })
      this.endTurn(now)
      return
    }

    if (before === TrapStatus.UNSEEN) {
      // Первое знакомство: объяснение и видео, затем падение на дно, R62-R66.
      this.fall(player, swamp)
      path.push(player.node)
      this.recordTrap(player, now, {
        event: TurnEvent.TRAP_FIRST,
        dice,
        holdMs,
        from,
        path,
        trapId: swamp.trapId,
        before,
        after: TrapStatus.SEEN,
        explanation: this.trapsById[swamp.trapId]?.explanation ?? null,
      })
      this.endTurn(now, 'notice')
      return
    }

    // Статус SEEN: сразу задаём вопрос категории этого болота, R70, R72.
    // Очередь не передаётся, пока ответ не получен, R37.
    this.state.pending = {
      trapId: swamp.trapId,
      questionId: this.drawQuestion(swamp.trapId),
      teleportKind: null,
      teleportTarget: null,
      from,
      path,
      dice,
    }
    this.setPhase(Phase.AWAIT_ANSWER, this.state.settings.answerMs, now)
    this.broadcastSnapshot(now)
  }

  /**
   * Исход вопроса ловушки или телепорта.
   *
   * Сюда сходятся оба конца: присланный ответ и истёкшее время. Разница между
   * ними в одном — в событии хода; всё остальное совпадает, поэтому верный
   * ответ оставляет игрока на входе болота и даёт опыт, а любой другой исход
   * роняет на дно так же, как первое попадание, R42, R78.
   */
  private closeQuestion(player: Player, answerId: string | null, timedOut: boolean, now: number): void {
    const pending = this.state.pending
    const trapId = pending?.trapId ?? null
    const question = pending?.questionId ? this.questionsById[pending.questionId] : null
    const swamp = this.board.swamps.find((s) => s.trapId === trapId)
    const correct = !timedOut && !!question && answerId !== null && answerId === question.correctAnswerId

    const from = pending?.from ?? player.node
    const path = pending?.path ?? []

    if (pending?.teleportKind) {
      const teleport =
        pending.teleportTarget &&
        ((pending.teleportKind === 'boost' && correct) || (pending.teleportKind === 'throw' && !correct))
      if (teleport) {
        player.node = pending.teleportTarget!
        path.push(player.node)
      }
      const landed = pending.teleportKind === 'boost' ? CellType.LUCK : CellType.SETBACK
      this.record(
        {
          playerId: player.id,
          event: timedOut ? TurnEvent.ANSWER_TIMEOUT : correct ? TurnEvent.ANSWER_WIN : TurnEvent.ANSWER_FAIL,
          dice: pending.dice,
          holdMs: null,
          from,
          to: player.node,
          path,
          landed,
          questionId: pending.questionId,
          ...(timedOut
            ? { explanation: question?.explanation ?? null }
            : {
                answerId,
                correctAnswerId: question?.correctAnswerId ?? null,
                correct,
                explanation: question?.explanation ?? null,
              }),
        },
        now,
      )
      // Рывок может привести на финиш: победа, R28.
      if (this.board.nodes.find((n) => n.id === player.node)?.type === CellType.SUCCESS) {
        player.finishedTurn = this.state.turnNumber
        this.state.winnerId = player.id
        this.record(
          {
            playerId: player.id,
            event: TurnEvent.FINISH,
            dice: null,
            holdMs: null,
            from,
            to: player.node,
            path: [],
            landed: CellType.SUCCESS,
          },
          now,
        )
        this.finish(now)
        return
      }
      this.endTurn(now, 'answer')
      return
    }

    if (!correct && swamp) {
      this.fall(player, swamp)
      path.push(player.node)
    }
    this.recordTrap(player, now, {
      event: timedOut ? TurnEvent.ANSWER_TIMEOUT : correct ? TurnEvent.ANSWER_WIN : TurnEvent.ANSWER_FAIL,
      dice: pending?.dice ?? null,
      from,
      path,
      trapId,
      before: TrapStatus.SEEN,
      after: correct ? TrapStatus.MASTERED : TrapStatus.SEEN,
      questionId: pending?.questionId ?? null,
      // Ответа не было: ни выбора, ни верного ответа в записи нет (R78), а объяснение есть при любом исходе, R79.
      ...(timedOut
        ? { explanation: question?.explanation ?? null }
        : {
            answerId,
            correctAnswerId: question?.correctAnswerId ?? null,
            correct,
            explanation: question?.explanation ?? null,
          }),
    })
    this.endTurn(now, 'answer')
  }

  /**
   * Конец хода. После первого падения (`notice`) ждём показ хода и окно уведомления, после ответа (`answer`)
   * окно итога и показ последствия; сроки ставит сервер, подтверждения от клиента не нужно, R114.
   */
  private endTurn(now: number, after?: 'notice' | 'answer'): void {
    this.state.pending = null
    if (this.state.phase === Phase.FINISHED) return
    const { resultMs, resultWindowMs, noticeWindowMs, showMs } = this.state.settings
    const wait =
      after === 'notice' ? showMs + noticeWindowMs : after === 'answer' ? resultWindowMs + showMs : resultMs
    this.setPhase(Phase.TURN_RESULT, wait, now)
    if (after === 'answer') this.state.resultEndsAt = now + resultWindowMs
    this.broadcastSnapshot(now)
  }

  private nextTurn(now: number): void {
    if (!this.state.order.length) return
    const i = this.state.order.indexOf(this.state.currentPlayerId ?? this.state.order[0])
    this.state.currentPlayerId = this.state.order[(i + 1) % this.state.order.length]
    this.beginTurn(now)
  }

  /** Падение на дно болота. Промежуточные клетки не проходятся, R66. */
  private fall(player: Player, swamp: SwampPlan): void {
    player.node = swampNodeId(swamp.entry, swamp.depth)
  }

  private finish(now: number): void {
    this.state.pending = null
    this.setPhase(Phase.FINISHED, null, now)
    this.broadcast({
      type: 'gameFinished',
      winnerId: this.state.winnerId,
      standings: this.standings(),
    })
    this.broadcastSnapshot(now)
  }

  /** Игроки по местам: дальше по линии лучше, глубина в болоте хуже. */
  private standings(): PlayerId[] {
    const rank = (id: PlayerId): number => {
      const pos = positionOf(this.state.players[id].node)
      return pos.cellIndex * 100 - pos.depth
    }
    return [...this.state.order].sort((a, b) => rank(b) - rank(a))
  }

  // --- вопросы --------------------------------------------------------------

  /**
   * Вопрос ловушки берётся из категории болота (R81); null выбирает общий пул телепортов.
   * Показанный вопрос выбывает из партии для всех игроков, R83.
   * Если выбранный пул исчерпан, показанные вопросы возвращаются в игру, R84.
   */
  private drawQuestion(trapId: string | null): string | null {
    const bank = trapId ? (this.banks[trapId] ?? []) : this.allQuestionIds
    if (!bank.length) return null
    const used = usedQuestionIds(this.state.turns)
    let fresh = bank.filter((id) => !used.includes(id))
    if (!fresh.length) fresh = [...bank]
    return fresh[Math.floor(this.random() * fresh.length)]
  }

  // --- вспомогательное ------------------------------------------------------

  private playerList(): Player[] {
    return this.state.order.map((id) => this.state.players[id]).filter((p): p is Player => !!p)
  }

  private activePlayer(): Player | null {
    const id = this.state.currentPlayerId
    return id ? (this.state.players[id] ?? null) : null
  }

  /**
   * Вход в партию состоялся: игрок получает свой снапшот с токеном, остальные
   * — обновлённый общий. Путей сюда два — вернулся по токену и вошёл впервые,
   * — и различаются они только тем, откуда взялся токен.
   */
  private welcome(playerId: PlayerId, token: string, now: number): { ok: true } {
    this.bump(now)
    this.send(playerId, {
      type: 'joined',
      playerId,
      sessionToken: token,
      snapshot: this.snapshot(now),
    })
    this.broadcastSnapshot(now)
    return { ok: true }
  }

  /**
   * Хозяин ушёл: комнату принимает первый, кто на связи. Случается это и при
   * выходе, и при обрыве, и правило в обоих местах обязано быть одним.
   */
  private passHost(): void {
    const next = this.playerList().find((p) => p.connected)
    if (next) next.isHost = true
  }

  private hasHost(): boolean {
    return this.playerList().some((p) => p.isHost)
  }

  /**
   * Запись хода про ловушку.
   *
   * Все четыре исхода — попадание на освоенную, первое знакомство, ответ и
   * молчание — заполняют одни и те же поля: кто, откуда, каким путём, на
   * какой ловушке и как сменился её статус. Здесь они заданы один раз:
   * иначе новое поле журнала дописывается в четыре ветки, и пропуск в одной
   * из них виден только дыркой в истории партии.
   */
  private recordTrap(
    player: Player,
    now: number,
    trap: {
      event: TurnEvent
      from: NodeId
      path: NodeId[]
      trapId: string | null
      before: TrapStatus
      after: TrapStatus
    } & Partial<TurnRecord>,
  ): void {
    const { event, from, path, trapId, before, after, ...rest } = trap
    this.record(
      {
        playerId: player.id,
        event,
        dice: null,
        holdMs: null,
        from,
        to: player.node,
        path,
        landed: CellType.TRAP,
        trapId,
        trapStatusBefore: before,
        trapStatusAfter: after,
        trapTitle: (trapId && this.trapsById[trapId]?.title) || null,
        ...rest,
      },
      now,
    )
  }

  private record(
    partial: Pick<TurnRecord, 'playerId' | 'event' | 'dice' | 'holdMs' | 'from' | 'to' | 'path' | 'landed'> &
      Partial<TurnRecord>,
    now: number,
  ): void {
    const rec: TurnRecord = {
      turn: this.state.turnNumber,
      trapId: null,
      trapStatusBefore: null,
      trapStatusAfter: null,
      questionId: null,
      answerId: null,
      correctAnswerId: null,
      correct: null,
      trapTitle: null,
      explanation: null,
      at: now,
      ...partial,
    }
    this.state.turns.push(rec)
    this.broadcast({ type: 'turnResolved', record: rec })
  }

  private pickPawn(preferred?: PawnId): PawnId {
    const taken = new Set(this.playerList().map((p) => p.pawn))
    if (preferred && !taken.has(preferred)) return preferred
    return (PAWNS.find((p) => !taken.has(p.id))?.id ?? PAWNS[0].id) as PawnId
  }

  private setPhase(phase: Phase, durationMs: number | null, now: number): void {
    this.state.phase = phase
    this.state.phaseEndsAt = durationMs === null ? null : now + durationMs
    this.state.resultClosed = false
    this.state.resultEndsAt = null
    this.bump(now)
  }

  private bump(now: number): void {
    this.state.revision++
    this.state.updatedAt = now
  }

  private remember(playerId: PlayerId, commandId: string): void {
    const list = (this.state.appliedCommands[playerId] ??= [])
    list.push(commandId)
    if (list.length > 100) list.splice(0, list.length - 100)
  }

  private send(to: PlayerId, event: ServerEvent): void {
    this.outbox.push({ to, event })
  }

  private broadcast(event: ServerEvent): void {
    this.outbox.push({ to: '*', event })
  }

  private broadcastSnapshot(now: number): void {
    this.syncAutoStart(now)
    this.broadcast({ type: 'snapshot', snapshot: this.snapshot(now) })
  }

  /** Снапшот и граница видимости. Вопрос без ответа видят все: диалоги идут синхронно, R86. */
  snapshot(now: number = Date.now()): Snapshot {
    now = this.clockAt(now)
    const pending = this.state.pending
    const visible = this.state.phase === Phase.AWAIT_ANSWER
    const question = visible && pending?.questionId ? (this.questionsById[pending.questionId] ?? null) : null

    return {
      gameId: this.state.gameId,
      code: this.state.code,
      roomName: this.state.roomName,
      phase: this.state.phase,
      revision: this.state.revision,
      serverNow: now,
      turnNumber: this.state.turnNumber,
      currentPlayerId: this.state.currentPlayerId,
      phaseEndsAt: this.state.phaseEndsAt,
      resultClosed: this.state.resultClosed,
      resultEndsAt: this.state.resultEndsAt,
      autoStartMin: this.state.autoStartMin,
      startsAt: this.state.startsAt,
      board: this.board,
      settings: this.state.settings,
      players: this.playerList(),
      winnerId: this.state.winnerId,
      standings: this.standings(),
      activeQuestion: question ? toPublicQuestion(question) : null,
      pendingMove:
        this.state.phase === Phase.AWAIT_ANSWER && pending?.from
          ? { from: pending.from, path: [...pending.path], dice: pending.dice }
          : null,
      recentTurns: this.state.turns.slice(-10),
    }
  }
}

function generateCode(seed: number): string {
  // Без похожих символов, чтобы код читался вслух.
  const alphabet = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'
  let code = ''
  for (let i = 0; i < 5; i++) code += alphabet[Math.floor(mulberry32(seed + i * 7919) * alphabet.length)]
  return code
}

/**
 * Запасной генератор токена для прямых тестов ядра. В бою не используется:
 * mulberry32 детерминирован и раскручивается из seed поля, поэтому токены
 * вычислялись бы наперёд. Боевой токен выдаёт транспорт через crypto.
 */
function generateToken(seed: number, extra: number): string {
  let token = ''
  for (let i = 0; i < 4; i++)
    token += Math.floor(mulberry32(seed + i + extra * 2 ** 20) * 2 ** 32).toString(36)
  return token
}

// ============================================================================
// 9. РЕЕСТР ПАРТИЙ
// ============================================================================
//
// Транспорта здесь нет. Socket.IO живёт в srv/index.ts и общается с
// ядром ровно через две точки: handle() принимает команду, drain() отдаёт
// накопленные события. Поэтому ядро запускается в тестах без сети — этим и
// пользуется tests/state.test.ts.

export interface RegistryOptions {
  content: Content
  rules?: BoardRules
  settings?: Partial<Settings>
  /** Сколько комнат можно держать сразу. По умолчанию `MAX_ROOMS`. */
  maxRooms?: number
}

/** Партии в памяти процесса. Состояние сериализуемо, внешнее хранилище подключается позже, R94. */
export class GameRegistry {
  private games: Record<GameId, Game> = {}
  private byCode: Record<string, GameId> = {}
  private playerGame: Record<PlayerId, GameId> = {}

  constructor(private opts: RegistryOptions) {}

  /** Новую комнату создавать нельзя: места под неё нет, R118. */
  get isFull(): boolean {
    return Object.keys(this.games).length >= (this.opts.maxRooms ?? MAX_ROOMS)
  }

  create(seed?: number, roomName?: string): Game {
    // Фиксированный seed поля одинаков у всех комнат, а код и идентификатор считаются из seed: без поправки каждая новая
    // комната затирала бы прежнюю. Сдвигаем seed личности, пока код не окажется свободным; поле остаётся по seed.
    let identity: { gameId: string; code: string } | undefined
    if (seed !== undefined)
      for (let n = 0; ; n++) {
        const shifted = seed + n * 104729
        const code = generateCode(shifted)
        if (this.byCode[code] === undefined) {
          identity = n === 0 ? undefined : { gameId: `g_${shifted.toString(36)}`, code }
          break
        }
      }
    const game = new Game({
      content: this.opts.content,
      rules: this.opts.rules,
      settings: this.opts.settings,
      seed,
      roomName,
      ...identity,
    })
    this.games[game.id] = game
    this.byCode[game.code] = game.id
    return game
  }

  /**
   * Все живые комнаты. Начавшиеся тоже попадают в список: войти в них игроком
   * нельзя, но можно смотреть (R112), поэтому решение принимает клиент по флагу.
   */
  listRooms(): RoomSummary[] {
    return Object.values(this.games).map((g) => ({
      code: g.state.code,
      roomName: g.state.roomName,
      players: g.state.order.length,
      maxPlayers: g.state.settings.maxPlayers,
      seats: g.state.order.map((id) => {
        const p = g.state.players[id]!
        return { name: p.name, pawn: p.pawn, avatar: p.avatar }
      }),
      autoStartMin: g.state.autoStartMin,
      started: g.phase !== Phase.LOBBY,
      finished: g.phase === Phase.FINISHED,
    }))
  }

  /** Занято ли имя игрока где-нибудь на сервере. Проверка глобальная. */
  isNameTaken(name: string): boolean {
    const wanted = cleanText(name).toLowerCase()
    if (!wanted) return false
    return Object.values(this.games).some((g) =>
      Object.values(g.state.players).some((p) => p.connected && p.name.trim().toLowerCase() === wanted),
    )
  }

  get(gameId: GameId): Game | undefined {
    return this.games[gameId]
  }
  getByCode(code: string): Game | undefined {
    const id = this.byCode[code.trim().toUpperCase()]
    return id ? this.games[id] : undefined
  }
  gameOfPlayer(playerId: PlayerId): Game | undefined {
    const id = this.playerGame[playerId]
    return id ? this.games[id] : undefined
  }
  bind(playerId: PlayerId, gameId: GameId): void {
    this.playerGame[playerId] = gameId
  }
  /** Удалить партию и освободить её код и привязки игроков. */
  discard(gameId: GameId): void {
    const game = this.games[gameId]
    if (!game) return
    delete this.byCode[game.code]
    delete this.games[gameId]
    for (const playerId of game.state.order)
      if (this.playerGame[playerId] === gameId) delete this.playerGame[playerId]
  }
  unbind(playerId: PlayerId): void {
    delete this.playerGame[playerId]
  }

  /** Двигает дедлайны всех партий. Комнаты удаляются только явной командой. */
  tickAll(now: number = Date.now()): Array<{ gameId: GameId; out: Outgoing[] }> {
    const batches: Array<{ gameId: GameId; out: Outgoing[] }> = []
    for (const [gameId, game] of Object.entries(this.games)) {
      game.tick(now)
      const out = game.drain()
      if (out.length) batches.push({ gameId, out })
    }
    return batches
  }
}
